import React, { forwardRef, useCallback, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { Button, Checkbox, Tag, Tooltip, Typography } from 'antd'
import moment from 'moment'

import * as Icons from '@ant-design/icons'
import _ from 'lodash'
import { useWatch } from 'react-hook-form'
import { useParams, generatePath } from 'react-router-dom'

import * as ROUTES from 'admin/routes'
import * as Rules from 'admin/lib/ValidationRules'

import { FormRow, FormItem, FormItemLayout, Select } from 'admin/lib/Forms'
import { Delivery as fields } from 'admin/lib/Records'
import { Queries } from 'admin/lib/Api'
import { getCertStatus } from 'admin/lib/Certificates/CertificateExpiry'

import { Guid } from 'admin/lib/guid'
import { RAW } from 'admin/pages/RecordEdit/common/constants'

function ReferenceProductItemSelectInner(
  {
    lineItem,
    namePrefix,
    onChange,
    dataView = RAW,
    showRaw = false,
    isDisabled = (prop1, prop2, prop3) => false,
    isHidden = (prop1, prop2, prop3) => false,
    isExtendedPGList = prop1 => false,
    productName
  },
  ref
) {
  const { accountId, projectId } = useParams()
  const selectedReferenceProductItemId = useWatch({
    name: `${namePrefix}referenceProductId`,
    defaultValue: lineItem.referenceProductId
  })

  const [refProdSearch, setRefProdSearch] = useState('')
  const [productGroupValue, setProductGroupValue] = useState(
    lineItem.productGroups
  )
  const [isHiredOrRentedChecked, setIsHiredOrRentedChecked] = useState(
    lineItem.isHiredOrRented
  )

  const referenceProductItems = Queries.useReferenceProductItems(refProdSearch)

  const selectedReferenceProductItem = Queries.useReferenceProductItem(
    selectedReferenceProductItemId
  )
  const allCertificates = Queries.useCertificates()
  const allCompanies = Queries.useCompanies()
  const allProductGroups = Queries.useProductGroups({
    accountId,
    projectId
  })

  const loading =
    !referenceProductItems.isFetched ||
    !selectedReferenceProductItem.isFetched ||
    !allCertificates.isFetched ||
    !allCompanies.isFetched ||
    !allProductGroups.isFetched

  const referenceProductItemTree = useMemo(() => {
    if (loading || !referenceProductItems.data?.pages || !allCompanies.data) {
      return []
    }

    return _(referenceProductItems.data.pages)
      .flatMap(page => page.items)
      .groupBy(rpi => rpi.name)
      .values()
      .map(rpiGroup =>
        rpiGroup.map(rpi => {
          const companies =
            rpi.companyIds?.map(companyId =>
              allCompanies.data?.find(
                company => company.companyId === companyId
              )
            ) ?? []

          return {
            product: rpi,
            companies,
            content:
              companies.length > 0
                ? companies
                    .map(company => company?.name ?? 'Unknown Company')
                    .join(', ')
                : '(No Companies)'
          }
        })
      )
      .value()
  }, [allCompanies.data, loading, referenceProductItems.data])

  const certificates = useMemo(() => {
    if (
      !selectedReferenceProductItem.data ||
      !allCertificates.data ||
      loading
    ) {
      return []
    }

    return allCertificates.data
      .filter(cert =>
        selectedReferenceProductItem.data?.certificateIds?.includes(
          cert.certificateId
        )
      )
      .map(cert => ({
        ...cert,
        status: getCertStatus(cert),
        expirationDate: moment(cert.expirationDate).local().format('DD/MM/YYYY')
      }))
  }, [allCertificates.data, loading, selectedReferenceProductItem.data])

  const companies = useMemo(() => {
    if (!selectedReferenceProductItem.data || loading || !allCompanies.data) {
      return []
    }

    return allCompanies.data.filter(company =>
      selectedReferenceProductItem.data?.companyIds?.includes(company.companyId)
    )
  }, [allCompanies.data, loading, selectedReferenceProductItem.data])

  const referenceProductGroups = useMemo(() => {
    if (!allProductGroups.data || loading) {
      if (lineItem.productGroups) {
        return [
          {
            value: lineItem.productGroups,
            label: lineItem.productGroups.slice(
              lineItem.productGroups.indexOf(':') + 1
            )
          }
        ]
      }
      return []
    }

    const filteredGroups = allProductGroups.data.map(rpg => {
      return {
        value: `${rpg.productGroupId}:${rpg.name}`,
        label: rpg.name,
        className: rpg.name.replace(/[\s&()]+/g, '-').toLowerCase(),
        title: rpg.description
      }
    })

    const deDupedGroups = [
      ...new Map(filteredGroups.map(v => [v.value, v])).values()
    ]

    return deDupedGroups
  }, [allProductGroups.data, lineItem.productGroups, loading])

  const handleReferenceProductOnSelect = (value, fieldId) => {
    const mockEvent = {
      target: {
        name: fieldId,
        value: value
      }
    }

    onChange([mockEvent])
  }

  const handleProductGroupOnSelect = (value, fieldId) => {
    setProductGroupValue(value)
    const mockEvent = {
      target: {
        name: fieldId,
        value: value
      }
    }
    onChange(mockEvent)
  }

  const handleProductGroupOnClear = fieldId => {
    setProductGroupValue(null)
    const mockEvent = {
      target: {
        name: fieldId,
        value: null
      }
    }
    onChange(mockEvent)
  }

  const handleIsHiredOrRentedChange = event => {
    const mockEvent = {
      target: {
        name: `${namePrefix}${fields.lineItems.isHiredOrRented.path}`,
        value: !isHiredOrRentedChecked
      }
    }
    onChange(mockEvent)

    setIsHiredOrRentedChecked(prev => !prev)
  }

  const hasProductCategoriserReason = () => {
    if (lineItem.productCategoriserReason) {
      return (
        <span>
          {' '}
          <Tooltip title={lineItem.productCategoriserReason}>
            <Icons.InfoCircleOutlined />
          </Tooltip>
        </span>
      )
    }
    return ''
  }

  const clearCompaniesFilterSorter = () => {
    localStorage.removeItem('companiesFilters')
    localStorage.removeItem('companiesSorter')
  }

  const lookupFunction = useCallback(() => {
    const pgString = referenceProductGroups.map(rpg => rpg.label).join(', ')
    const supplier = document.getElementsByName('raw.supplierName')[0].value

    const string = `Construction material classification for ${productName} from ${supplier} - determine which of these product groups it belongs to (${pgString})`
    const encodedString = encodeURIComponent(string)

    window.open(`https://www.google.com/search?q=${encodedString}`, '_blank')
  }, [productName, referenceProductGroups])

  return (
    <>
      <FormRow>
        <FormItem
          label={fields.lineItems.referenceProductId.label}
          name={namePrefix + fields.lineItems.referenceProductId.path}
          altNames={[namePrefix + fields.lineItems.knownProductName.path]}
          defaultValue={lineItem.referenceProductId}
          rules={Rules.MandatoryString}
          inputRef={ref}
          disabled={isDisabled(
            fields.lineItems.referenceProductId.isRawField,
            dataView,
            showRaw
          )}
          hidden={isHidden(
            fields.lineItems.referenceProductId.isRawField,
            dataView,
            showRaw
          )}
          instrumented={true}
        >
          <Select
            allowClear
            placeholder="Select product type"
            style={{ width: '100%' }}
            loading={
              !referenceProductItems.isFetched ||
              referenceProductItems.isFetchingNextPage
            }
            optionLabelProp="label"
            showSearch
            onSelect={value =>
              handleReferenceProductOnSelect(
                value,
                namePrefix + fields.lineItems.referenceProductId.path
              )
            }
            onSearch={value => setRefProdSearch(value)}
            filter={false}
            onBottomReached={() => {
              if (referenceProductItems.hasNextPage) {
                referenceProductItems.fetchNextPage()
              }
            }}
          >
            <Select.Option
              aria-label={'(Create after saving)'}
              value={Guid.Empty}
              label="(Create after saving)"
            >
              (Create after saving)
            </Select.Option>

            {selectedReferenceProductItemId &&
              Guid.Empty !== selectedReferenceProductItemId && (
                <Select.Option
                  aria-label=""
                  key="current-value"
                  value={selectedReferenceProductItemId}
                  label={`${
                    selectedReferenceProductItem.data?.name ?? 'Unknown'
                  }`}
                >
                  {`Current: ${
                    selectedReferenceProductItem.data?.name ?? 'Unknown'
                  }`}
                </Select.Option>
              )}

            {referenceProductItemTree.map(rpiGroup => {
              const isGroup = rpiGroup.length > 1
              const firstProduct = rpiGroup[0].product

              return isGroup ? (
                <Select.OptGroup
                  key={firstProduct.name}
                  label={firstProduct.name}
                >
                  {rpiGroup.map(rpiGroupItem => {
                    return (
                      <Select.Option
                        key={rpiGroupItem.product.referenceProductId}
                        value={rpiGroupItem.product.referenceProductId}
                        label={firstProduct.name}
                        aria-label={rpiGroupItem.content}
                      >
                        {rpiGroupItem.content}
                      </Select.Option>
                    )
                  })}
                </Select.OptGroup>
              ) : (
                <Select.Option
                  key={firstProduct.name}
                  value={firstProduct.referenceProductId}
                  label={firstProduct.name}
                  aria-label={firstProduct.name}
                >
                  {firstProduct.name}
                </Select.Option>
              )
            })}

            {((referenceProductItems.isFetching &&
              referenceProductItemTree.length === 0) ||
              referenceProductItems.isFetchingNextPage) && (
              <Select.Option disabled aria-label="">
                <Icons.LoadingOutlined /> Loading...
              </Select.Option>
            )}
          </Select>
        </FormItem>
        {!isHidden(
          fields.lineItems.referenceProductId.isRawField,
          dataView,
          showRaw
        ) && (
          <Button
            type="text"
            tabIndex="-1"
            icon={<Icons.EditFilled />}
            href={ROUTES.REFERENCEITEMS.LIST}
            target="_blank"
          />
        )}
      </FormRow>

      {!isHidden(
        fields.lineItems.referenceProductId.isRawField,
        dataView,
        showRaw
      ) && (
        <>
          <FormRow>
            <FormItemLayout disabled label={`Companies (readonly)`}>
              <div>
                {loading && <Icons.LoadingOutlined />}

                {companies.length > 0 &&
                  companies.map(company => (
                    <Tag key={company.companyId}>{company.name}</Tag>
                  ))}

                {!loading && companies.length === 0 && (
                  <Typography.Text type="secondary">
                    No Companies
                  </Typography.Text>
                )}
              </div>
            </FormItemLayout>

            <Button
              type="text"
              tabIndex="-1"
              icon={<Icons.EditFilled />}
              onClick={clearCompaniesFilterSorter}
              href={ROUTES.COMPANIES.LIST}
              target="_blank"
            />
          </FormRow>

          <FormRow>
            <FormItemLayout
              disabled
              name={`${namePrefix}${fields.lineItems.knownCertifications.path}`}
              label={`${fields.lineItems.knownCertifications.label} (readonly)`}
            >
              <div>
                {loading && <Icons.LoadingOutlined />}

                {certificates.length > 0 &&
                  certificates.map(cert => (
                    <Tag
                      key={cert.certificateId}
                      color={cert.status?.color}
                      title={
                        cert.status?.text &&
                        `${cert.status?.text}: ${cert.expirationDate}`
                      }
                    >
                      {cert.certificationScheme} {cert.certificationRating}{' '}
                      {cert.certificateNumber}
                    </Tag>
                  ))}

                {!loading && certificates.length === 0 && (
                  <Typography.Text type="secondary">
                    No Certificates
                  </Typography.Text>
                )}
              </div>
            </FormItemLayout>

            <Button
              type="text"
              tabIndex="-1"
              icon={<Icons.EditFilled />}
              href={generatePath(ROUTES.REFERENCEITEMS.EDIT, {
                referenceItemId: selectedReferenceProductItemId
              })}
              target="_blank"
            />
          </FormRow>
        </>
      )}
      <FormRow
        style={
          !isHidden(
            fields.lineItems.productGroups.isRawField,
            dataView,
            showRaw
          ) && isExtendedPGList(referenceProductGroups)
            ? { border: 'solid 3px hotpink' }
            : null
        }
      >
        <FormItemLayout
          name={`${namePrefix}${fields.lineItems.productGroups.path}`}
          label={
            <span>
              {fields.lineItems.productGroups.label}{' '}
              {hasProductCategoriserReason()}
            </span>
          }
          disabled={isDisabled(
            fields.lineItems.productGroups.isRawField,
            dataView,
            showRaw
          )}
          hidden={isHidden(
            fields.lineItems.productGroups.isRawField,
            dataView,
            showRaw
          )}
        >
          {!isHidden(
            fields.lineItems.productGroups.isRawField,
            dataView,
            showRaw
          ) && (
            <Select
              allowClear
              placeholder="Select Product Group"
              value={productGroupValue}
              showSearch
              optionFilterProp="label"
              options={referenceProductGroups}
              onSelect={value =>
                handleProductGroupOnSelect(
                  value,
                  `${namePrefix}${fields.lineItems.productGroups.path}`
                )
              }
              onClear={() =>
                handleProductGroupOnClear(
                  `${namePrefix}${fields.lineItems.productGroups.path}`
                )
              }
              instrumented={true}
              fieldName={`${namePrefix}${fields.lineItems.productGroups.path}`}
              listHeight={352}
              lookupFunction={lookupFunction}
              lookupTooltipText="Ask Google to suggest a Product Group"
            />
          )}
        </FormItemLayout>
      </FormRow>

      <FormRow
        style={
          isHidden(
            fields.lineItems.isHiredOrRented.isRawField,
            dataView,
            showRaw
          )
            ? { display: 'none' }
            : {}
        }
      >
        <FormItemLayout
          label="&nbsp;"
          name={`${namePrefix}${fields.lineItems.isHiredOrRented.path}`}
          style={{ maxWidth: 0 }}
        >
          <input type="hidden" />
        </FormItemLayout>

        <Checkbox
          checked={isHiredOrRentedChecked}
          onChange={handleIsHiredOrRentedChange}
          style={{ paddingBottom: '6px' }}
          disabled={isDisabled(
            fields.lineItems.isHiredOrRented.isRawField,
            dataView,
            showRaw
          )}
        >
          {`${fields.lineItems.isHiredOrRented.label}`}
        </Checkbox>
      </FormRow>
    </>
  )
}

export const ReferenceProductItemSelect = forwardRef(
  ReferenceProductItemSelectInner
)

ReferenceProductItemSelectInner.propTypes = {
  lineItem: PropTypes.object,
  namePrefix: PropTypes.string.isRequired,
  onChange: PropTypes.func
}
